(() => {
  const controllers = new Map();
  const mobile = matchMedia('(max-width: 749px)');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  function init(root = document) {
    root.querySelectorAll('[data-vn-shelf]').forEach(s => {
      if (controllers.has(s)) return;
      const track = s.querySelector('[data-vn-track]');
      if (!track) return;
      const abort = new AbortController();
      const prev = s.querySelector('[data-vn-prev]');
      const next = s.querySelector('[data-vn-next]');
      const update = () => {
        if (prev) prev.disabled = track.scrollLeft < 3;
        if (next) next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 3;
      };
      [prev,next].filter(Boolean).forEach(b => b.addEventListener('click', e => {
        const left = track.clientWidth * (b === prev ? -1 : 1);
        const moving = e.detail !== 0 && s.dataset.vnMotion !== 'false' && document.body.dataset.vnMotion !== 'false' && !reduced.matches;
        track.scrollBy({left, behavior:moving ? 'smooth' : 'auto'});
      }, {signal:abort.signal}));
      track.addEventListener('scroll', update, {passive:true,signal:abort.signal});
      const resize = new ResizeObserver(update);
      resize.observe(track);
      s.addEventListener('shopify:block:select', e => {
        const item = e.target.closest('.vn-shelf-item');
        if (item) track.scrollTo({left:item.offsetLeft - track.firstElementChild.offsetLeft,behavior:'auto'});
      }, {signal:abort.signal});
      controllers.set(s, () => { resize.disconnect(); abort.abort(); });
      update();
    });
    root.querySelectorAll('[data-vn-films]').forEach(s => {
      if (controllers.has(s)) return;
      const abort = new AbortController();
      const listen = (el, name, fn, options = {}) => el.addEventListener(name, fn, {...options, signal: abort.signal});
      const track = s.querySelector('[data-vn-film-track]');
      const slides = [...track.querySelectorAll('.vn-film')];
      const dots = [...s.querySelectorAll('[data-vn-film-dot]')];
      const videos = [...s.querySelectorAll('video')];
      const count = s.querySelector('[data-vn-film-count]');
      const pause = s.querySelector('[data-vn-film-pause]');
      const manuallyControlled = new WeakSet();
      let index = 0, timer, scrollTimer, hovered = false, sectionVisible = false;
      let stopped = s.dataset.vnRotate !== 'true', explicitResume = false;
      const moving = () => !reduced.matches && s.dataset.vnMotion !== 'false' && document.body.dataset.vnMotion !== 'false';
      const visible = v => {
        const r = v.getBoundingClientRect(), parent = v.closest('.vn-film');
        const onSlide = !mobile.matches || parent === slides[index];
        return onSlide && r.width > 0 && r.height > 0 && r.bottom > Math.min(r.height * .25, innerHeight * .25) &&
          r.top < innerHeight - Math.min(r.height * .25, innerHeight * .25) && r.right > 0 && r.left < innerWidth;
      };
      function syncVideos() {
        videos.forEach(v => {
          if (document.hidden || !visible(v)) { v.pause(); return; }
          if (v.autoplay && !manuallyControlled.has(v) && v.paused && !(v.ended && !v.loop)) {
            v.muted = true; v.defaultMuted = true; v.playsInline = true;
            const promise = v.play();
            if (promise) promise.catch(() => {});
          }
        });
      }
      function schedule() {
        clearTimeout(timer);
        if (pause) {
          pause.hidden = slides.length < 2 || !moving();
          pause.textContent = stopped ? s.dataset.vnPlayLabel : s.dataset.vnPauseLabel;
          pause.setAttribute('aria-label', pause.textContent);
        }
        if (count) count.setAttribute('aria-live', stopped ? 'polite' : 'off');
        if (!mobile.matches || stopped || !moving() || document.hidden || !sectionVisible ||
            (!explicitResume && (hovered || s.contains(document.activeElement))) || slides.length < 2) return;
        timer = setTimeout(() => go(index + 1, false), Math.max(8, Number(s.dataset.vnSeconds) || 12) * 1000);
      }
      function state() {
        slides.forEach((slide, i) => { slide.inert = mobile.matches && i !== index; });
        dots.forEach((dot, i) => dot.setAttribute('aria-pressed', String(i === index)));
        if (count) count.textContent = (index + 1) + ' / ' + slides.length;
      }
      function stop() { stopped = true; explicitResume = false; schedule(); }
      function go(next, user = true, immediate = false) {
        if (!slides.length) return;
        index = (next + slides.length) % slides.length;
        if (user) { stopped = true; explicitResume = false; }
        state();
        if (mobile.matches) {
          const x = slides[index].getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
          track.scrollTo({left: x, behavior: immediate || !moving() ? 'auto' : 'smooth'});
        }
        syncVideos(); schedule();
      }
      dots.forEach((dot, i) => listen(dot, 'click', e => go(i, true, e.detail === 0)));
      const prev = s.querySelector('[data-vn-film-prev]'), next = s.querySelector('[data-vn-film-next]');
      if (prev) listen(prev, 'click', e => go(index - 1, true, e.detail === 0));
      if (next) listen(next, 'click', e => go(index + 1, true, e.detail === 0));
      if (pause) listen(pause, 'click', () => { stopped = !stopped; explicitResume = !stopped; schedule(); });
      listen(s, 'keydown', e => {
        if (!mobile.matches || e.target.closest('video') || !e.target.closest('.vn-film-navigation')) return;
        const key = e.key;
        if (!['ArrowLeft','ArrowRight','Home','End'].includes(key)) return;
        e.preventDefault();
        go(key === 'Home' ? 0 : key === 'End' ? slides.length - 1 : index + (key === 'ArrowRight' ? 1 : -1), true, true);
        dots[index]?.focus();
      });
      listen(track, 'pointerdown', () => { if (mobile.matches) stop(); });
      videos.forEach(v => listen(v, 'pointerdown', () => { if (v.controls) { manuallyControlled.add(v); stop(); } }));
      videos.forEach(v => listen(v, 'keydown', () => { if (v.controls) { manuallyControlled.add(v); stop(); } }));
      listen(track, 'scroll', () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          if (!mobile.matches) return;
          const left = track.getBoundingClientRect().left;
          index = slides.reduce((best, slide, i) => Math.abs(slide.getBoundingClientRect().left - left) < Math.abs(slides[best].getBoundingClientRect().left - left) ? i : best, 0);
          state(); syncVideos();
        }, 120);
      }, {passive: true});
      listen(s, 'pointerenter', e => { if (e.pointerType === 'mouse') { hovered = true; schedule(); } });
      listen(s, 'pointerleave', () => { hovered = false; schedule(); });
      listen(s, 'focusin', e => { if (e.target !== pause) explicitResume = false; schedule(); });
      listen(s, 'focusout', () => setTimeout(schedule, 0));
      listen(document, 'visibilitychange', () => { syncVideos(); schedule(); });
      listen(mobile, 'change', () => go(index, false, true));
      listen(reduced, 'change', () => { syncVideos(); schedule(); });
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => { if (entry.target === s) sectionVisible = entry.isIntersecting; });
        syncVideos(); schedule();
      }, {threshold: [0, .25, .5]});
      observer.observe(s); videos.forEach(v => observer.observe(v));
      const resize = new ResizeObserver(() => { if (mobile.matches) go(index, false, true); });
      resize.observe(track);
      listen(s, 'shopify:block:select', e => {
        const i = slides.indexOf(e.target.closest('.vn-film'));
        if (i >= 0) go(i, true, true);
      });
      controllers.set(s, () => { clearTimeout(timer); clearTimeout(scrollTimer); observer.disconnect(); resize.disconnect(); abort.abort(); videos.forEach(v => v.pause()); });
      videos.forEach(v => { v.muted = true; v.defaultMuted = true; v.playsInline = true; });
      state(); syncVideos(); schedule();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', e => init(e.target));
  document.addEventListener('shopify:section:unload', e => {
    controllers.forEach((destroy, s) => { if (e.target.contains(s)) { destroy(); controllers.delete(s); } });
  });
})();
(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const fine = matchMedia('(min-width:750px) and (pointer:fine)');
  const observed = new Set();
  const animations = new Map();
  const heroes = new Set();
  let frame = 0;
  const allowed = el => !reduced.matches && document.body.dataset.vnMotion !== 'false' && !el.closest('[data-vn-motion="false"]');
  function finish(el) {
    animations.get(el)?.cancel();
    animations.delete(el);
    observer?.unobserve(el);
    observed.delete(el);
  }
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      observer.unobserve(el);
      observed.delete(el);
      if (!allowed(el) || !el.animate) return;
      const delay = el.closest('[data-vn-hero]') ? 70 : 0;
      const animation = el.animate([{opacity:.35,transform:'translateY(24px)'},{opacity:1,transform:'translateY(0)'}],{duration:850,delay,easing:'cubic-bezier(.22,1,.36,1)'});
      animations.set(el,animation);
      animation.finished.then(() => animations.delete(el)).catch(() => {});
    });
  }, {threshold:.08}) : null;
  function update() {
    frame = 0;
    heroes.forEach(hero => {
      if (!hero.isConnected) { heroes.delete(hero); return; }
      const active = allowed(hero) && fine.matches && !document.hidden;
      if (!active) {
        hero.style.removeProperty('--vn-hero-scale');
        hero.style.removeProperty('--vn-hero-y');
        return;
      }
      const r=hero.getBoundingClientRect();
      if (r.bottom < 0 || r.top > innerHeight) return;
      const progress=Math.max(0,Math.min(1,-r.top/Math.max(r.height,1)));
      hero.style.setProperty('--vn-hero-scale',String(1.025 + progress*.065));
      hero.style.setProperty('--vn-hero-y',(progress*12)+'px');
    });
  }
  const schedule=() => { if (!frame) frame=requestAnimationFrame(update); };
  function init(root=document) {
    root.querySelectorAll('[data-vn-reveal],.vn-home .section-header,.vn-home .newsletter-content>.prose').forEach(el => {
      if (el.dataset.vnRevealed || !observer || !allowed(el)) return;
      el.dataset.vnRevealed='true';
      observed.add(el);
      observer.observe(el);
    });
    root.querySelectorAll('[data-vn-hero]').forEach(hero => heroes.add(hero));
    schedule();
  }
  function refresh() {
    animations.forEach((animation,el) => { if (!allowed(el)) finish(el); });
    if (reduced.matches) observed.forEach(finish);
    schedule();
  }
  addEventListener('scroll',schedule,{passive:true});
  addEventListener('resize',schedule,{passive:true});
  reduced.addEventListener('change',refresh);
  fine.addEventListener('change',refresh);
  document.addEventListener('visibilitychange',refresh);
  document.addEventListener('shopify:section:load',e => init(e.target));
  document.addEventListener('shopify:section:unload',e => {
    observed.forEach(el => { if (e.target.contains(el)) finish(el); });
    animations.forEach((a,el) => { if (e.target.contains(el)) finish(el); });
    heroes.forEach(el => { if (e.target.contains(el)) heroes.delete(el); });
  });
  document.addEventListener('shopify:block:select',e => {
    animations.forEach((a,el) => { if (e.target.contains(el) || el.contains(e.target)) finish(el); });
  });
  if (document.readyState==='loading') document.addEventListener('DOMContentLoaded',() => init(),{once:true});
  else init();
})();
